import { PlusOutlined } from "@ant-design/icons";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
  StyledSubTableContainer,
} from "@/common/styles";
import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import CommonDatePicker from "@/common/elements/DatePicker";
import Select from "@/common/elements/InputElements/Select";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import { useEffect, useRef, useState } from "react";
import TextField from "@/common/elements/InputElements/TextField";
import { Form } from "antd";
import {
  getOptionsFromRefList,
  hasCheckSecurity,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import dayjs from "dayjs";
import Modal from "@/common/elements/Modal";
import { v4 as uuidv4 } from "uuid";
import useDialog from "@/hooks/useDialog";
import Alert from "@/common/elements/Alerts";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SCHEMA_NAME,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { useSelector } from "react-redux";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import DynamicForm from "@/common/DynamicForm";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";

const InmateMedicationLogDetail = ({
  medicationData,
  medicationName,
  numOfBottle,
  updateLogData,
  startingPoint = "",
}) => {
  const currentUser = JSON.parse(localStorage.getItem("userDetails"));
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );

  const headers = [
    { key: "dateDispensed", text: "Date Dispensed" },
    { key: "timeDispensed", text: "Time Dispensed" },
    { key: "numberofPills", text: "# Pills" },
    { key: "stafLbl", text: "Staff Description" },
    { key: "comments", text: "Comments" },
  ];
  const [medicationLogForm] = Form.useForm();
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [isMedicationLogModal, setIsMedicationLogModal] = useState(false);
  const [medicationLog, setMedicationLog] = useState([]);
  const [barCodeStatusText, setBarCodeStatusText] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const { showDialog } = useDialog();

  // -----------------------------------------------------------------------
  // MedicationLogExtra — PCI's per-client dynamic "_U" columns for this
  // dispensation (MedicationLogInterface.asp's "Additional Log Information"
  // section, shown between Dispensation Information and Administrative
  // Information).
  // -----------------------------------------------------------------------
  const [medicationLogExtraFields, setMedicationLogExtraFields] = useState([]);
  const [medicationLogExtraFieldsReady, setMedicationLogExtraFieldsReady] =
    useState(false);
  const medicationLogExtraTypeMapRef = useRef({});
  const medicationLogExtraIdRef = useRef(0);
  // Only a real, previously-saved log entry has this — a locally-added-but-
  // unsaved row only has a client-generated uuid selectedRowId, not this.
  const [selectedMedicationLogId, setSelectedMedicationLogId] = useState(null);

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/MedicationLogExtra`)
      .then((response) => {
        const tableData = (response?.data || []).filter((d) =>
          d?.columnName?.endsWith("_U")
        );
        const converted = convertApiFields(tableData, "MedicationLogExtra");
        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        medicationLogExtraTypeMapRef.current = typeMap;
        setMedicationLogExtraFields(converted);
        setMedicationLogExtraFieldsReady(true);
      })
      .catch((error) => {
        console.error(
          "[InmateMedicationLogDetail] MedicationLogExtra field defs fetch failed:",
          error
        );
      });
  }, []);

  // Fetches the saved MedicationLogExtra row when editing an existing log
  // entry — a brand new entry has no medicationLogID yet.
  useEffect(() => {
    if (!medicationLogExtraFieldsReady) return;
    if (!isNonEmptyVariable(selectedMedicationLogId)) {
      medicationLogExtraIdRef.current = 0;
      return;
    }

    fetchExtraTableRecord("MedicationLogExtra", "medicationLogID", selectedMedicationLogId)
      .then((result) => {
        if (!result) return;
        medicationLogExtraIdRef.current = result.raw?.MedicationLogExtraID ?? 0;
        const withDates = Object.keys(result.values).reduce((acc, key) => {
          const val = result.values[key];
          const htmlType = medicationLogExtraTypeMapRef.current[key];
          const isDateField = htmlType === "date" || htmlType === "datetime";
          acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
          return acc;
        }, {});
        medicationLogForm.setFieldsValue(withDates);
      })
      .catch((error) => {
        console.error(
          "[InmateMedicationLogDetail] MedicationLogExtra fetch failed:",
          error
        );
      });
  }, [selectedMedicationLogId, medicationLogExtraFieldsReady]);

  const handleModalVisible = (val) => {
    setIsMedicationLogModal(!isMedicationLogModal);
    if (val == "edit") {
      setIsEdit(true);
    }
  };

  const handleAdd = () => {
    // A stale selectedMedicationLogId from a previously-viewed row would
    // otherwise make the MedicationLogExtra fetch effect above populate
    // this fresh Add form with the old row's extra values.
    setSelectedMedicationLogId(null);
    medicationLogExtraIdRef.current = 0;
    const extraFieldKeys = medicationLogExtraFields.map((f) => f.columnName);
    medicationLogForm.setFieldsValue({
      medicationID: null,
      medicationBarCode: null,
      inmateBar: null,
      dateTimeDispensed: dayjs(new Date()),
      creatorStaffID: null,
      dispensedByStaffID: currentUser?.staffId,
      numberofPills: null,
      comments: null,
      pinNo: bookings?.bookingId,
      dateTimeAdded: dayjs(new Date()),
      user: currentUser?.username,
      ...Object.fromEntries(extraFieldKeys.map((k) => [k, null])),
    });
    setSelectedRowId(null);
    handleModalVisible();
  };

  useEffect(() => {
    setMedicationLog(medicationData?.medicationLog || []);
  }, [medicationData]);

  useEffect(() => {
    updateLogData(medicationLog);
  }, [medicationLog]);

  useEffect(() => {
    onCheckBarCodeStatus();
  }, [isMedicationLogModal]);

  const patchSelectedData = (data) => {
    medicationLogForm.setFieldsValue({
      ...data,
      inmateBar: data?.medicationBarCode,
      medicationID: medicationName,
      dateTimeDispensed: data.dateDispensed
        ? dayjs(`${data.dateDispensed} ${data.timeDispensed}`)
        : null,
      dateTimeAdded: data.dateAdded
        ? dayjs(`${data.dateAdded} ${data.timeAdded}`)
        : null,
    });
    setSelectedRowId(data.medicationLogID || data.id);
    setSelectedMedicationLogId(data?.medicationLogID || null);
  };

  const saveMedicationLog = () => {
    medicationLogForm
      .validateFields()
      .then((values) => {
        (values.dateDispensed = values?.dateTimeDispensed
          ? dayjs(values?.dateTimeDispensed).format("MM/DD/YYYY")
          : null),
          (values.timeDispensed = values?.dateTimeDispensed
            ? dayjs(values?.dateTimeDispensed).format("HH:mm")
            : null);
        (values.dateAdded = values?.dateTimeAdded
          ? dayjs(values?.dateTimeAdded).format("MM/DD/YYYY")
          : null),
          (values.timeAdded = values?.dateTimeAdded
            ? dayjs(values?.dateTimeAdded).format("HH:mm")
            : null);

        delete values.dateTimeAdded;
        delete values.dateTimeDispensed;
        delete values.inmateBar;

        // Split MedicationLogExtra dynamic fields out of the form values —
        // nested onto medicationLogExtra[0] below rather than left flat on
        // values, where they'd otherwise leak into the main bookings save
        // payload. See pages/Bookings/index.jsx's strip-before-POST and
        // saveMedicationLogExtra.
        const extraFieldKeys = medicationLogExtraFields.map((f) => f.columnName);
        const extraData = {};
        extraFieldKeys.forEach((k) => {
          const val = values?.[k];
          const htmlType = medicationLogExtraTypeMapRef.current[k];
          extraData[k] = val
            ? htmlType === "date"
              ? dayjs(val).format("MM/DD/YYYY")
              : htmlType === "datetime"
                ? dayjs(val).format("MM/DD/YYYY HH:mm")
                : val
            : null;
          delete values[k];
        });

        if (selectedRowId) {
          setMedicationLog((prev) =>
            prev?.map((p) => {
              if (p?.medicationLogID !== selectedRowId && p?.id !== selectedRowId) {
                return p;
              }
              const existingExtra = p?.medicationLogExtra?.[0] || {};
              return {
                ...values,
                medicationLogID: selectedRowId,
                numberofPills: parseInt(values?.numberofPills || 0),
                medicationBarCode: parseInt(values?.medicationBarCode || 0),
                pinNo: parseInt(values?.pinNo || 0),
                medicationID: parseInt(values?.medicationID || 0),
                medicationLogExtra:
                  extraFieldKeys.length > 0
                    ? [
                        {
                          ...existingExtra,
                          ...extraData,
                          medicationLogExtraID:
                            existingExtra?.medicationLogExtraID ??
                            medicationLogExtraIdRef.current ??
                            0,
                          medicationLogID:
                            existingExtra?.medicationLogID ?? selectedMedicationLogId ?? 0,
                        },
                      ]
                    : p?.medicationLogExtra,
              };
            })
          );
        } else {
          setMedicationLog((prev) => [
            ...prev,
            {
              ...values,
              id: uuidv4(),
              numberofPills: parseInt(values?.numberofPills || 0),
              medicationBarCode: parseInt(values?.medicationBarCode || 0),
              pinNo: parseInt(values?.pinNo || 0),
              medicationID: parseInt(values?.medicationID || 0),
              medicationLogExtra:
                extraFieldKeys.length > 0
                  ? [{ ...extraData, medicationLogExtraID: 0, medicationLogID: 0 }]
                  : undefined,
            },
          ]);
        }
        handleModalVisible();
      })
      .catch((err) => console.log(err));
  };

  const saveConfirmation = () => {
    const balanceBottle =
      numOfBottle -
        parseInt(medicationLogForm.getFieldValue("numberofPills")) || 0;
    showDialog("info", {
      title: "Warning",
      content:
        "Setting the current number of pills in the bottle/packet to " +
        (balanceBottle > 0 ? balanceBottle : 0),
      confirmButtonText: "Ok",
      onConfirm: saveMedicationLog,
    });
  };

  const handleBarCodeOnChange = (item) => {
    onCheckBarCodeStatus();
  };

  const onCheckBarCodeStatus = () => {
    const inmateBar = Number(medicationLogForm.getFieldValue("inmateBar"));
    const medicationBarCode = Number(
      medicationLogForm.getFieldValue("medicationBarCode")
    );

    if (
      inmateBar !== "" &&
      medicationBarCode !== "" &&
      inmateBar !== 0 &&
      medicationBarCode !== 0 &&
      !isNaN(Number(inmateBar)) &&
      !isNaN(Number(medicationBarCode))
    ) {
      if (Number(inmateBar) === Number(medicationBarCode)) {
        setBarCodeStatusText("MATCH");
      }
      if (Number(inmateBar) !== Number(medicationBarCode)) {
        setBarCodeStatusText("NO MATCH");
      }
    } else {
      setBarCodeStatusText("");
    }
  };

  useEffect(() => {
    if (isEdit) {
      let isShow = hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.MODIFY,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Inmate_Medical
      );
      if (isShow) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else {
      setIsDisabled(false);
    }
  }, [isEdit]);

  return (
    <>
      <StyledSectionHeading>
        <span>Medication Log</span>
      </StyledSectionHeading>

      <StyledSubTableContainer>
        <StyledFlex justify="end" marginBottom="10px" gap="10px">
          <Button
            icon={<PlusOutlined />}
            onClick={handleAdd}
            disabled={
              !bookingsFieldsEnabled ||
              !hasCheckSecurity(
                Security_Types.functionality,
                Entity_types.subModule,
                LABELS_CAPITAL.ADD,
                MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                SUB_MODULES_LIST_WITH_ID.Inmate_Medical
              )
            }
          >
            Add
          </Button>
          <Button
            onClick={handleModalVisible}
            disabled={
              !selectedRowId ||
              !hasCheckSecurity(
                Security_Types.functionality,
                Entity_types.subModule,
                LABELS_CAPITAL.VIEW,
                MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                SUB_MODULES_LIST_WITH_ID.Inmate_Medical
              )
            }
          >
            Details
          </Button>
        </StyledFlex>

        {hasCheckSecurity(
          Security_Types.functionality,
          Entity_types.subModule,
          LABELS_CAPITAL.VIEW,
          MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
          SUB_MODULES_LIST_WITH_ID.Inmate_Medical
        ) && (
          <Table
            headers={headers}
            data={medicationLog.map((item) => {
              return {
                ...item,
                stafLbl: getOptionsFromRefList("", true)?.filter(
                  (data) => data.value === item.dispensedByStaffID
                )?.[0]?.label,
              };
            })}
            onRowClick={patchSelectedData}
            onRowDoubleClick={(data) => {
              patchSelectedData(data);
              handleModalVisible();
            }}
          />
        )}
      </StyledSubTableContainer>

      <Modal
        header={"Medication Log"}
        show={isMedicationLogModal}
        size="lg"
        onHide={handleModalVisible}
        footerActions={[
          {
            key: "save",
            text: "Save",
            onClick: saveConfirmation,
            disabled: isDisabled || !bookingsFieldsEnabled,
          },
        ]}
        footerCloseAction={() => setIsEdit(false)}
        footerCloseActionText={"Close"}
      >
        {/* FieldSecurityProvider wraps the entire modal Form */}
        <FieldSecurityProvider
          entity={SCHEMA_NAME.MEDICATION_LOG}
          startingPoint={startingPoint}
          bookingsFieldsEnabled={bookingsFieldsEnabled}
        >
          <Form form={medicationLogForm}>

            {/* ── Medication Information ───────────────────────────── */}
            <StyledSectionHeading>
              <span>Medication Information</span>
            </StyledSectionHeading>
            <StyledGridRow gutter={16}>
              <StyledGridCol lg={5}>
                {/* Always disabled display field — plain FormItem */}
                <FormItem label="Medication" name="medicationID">
                  <TextField disabled placeholder={medicationName} />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol lg={6}>
                <SecureFormItem
                  fieldName="inmateBar"
                  formItemProps={{ name: "inmateBar", label: "Inmate Bar Code" }}
                >
                  <TextField type={"number"} onChange={handleBarCodeOnChange} />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={6}>
                <SecureFormItem
                  fieldName="medicationBarCode"
                  formItemProps={{
                    name: "medicationBarCode",
                    label: "Medication Bar Code",
                    dependencies: ["inmateBar"],
                    hasFeedback: true,
                    rules: [
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (getFieldValue("inmateBar") == value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Code is Mismatching"));
                        },
                      }),
                    ],
                  }}
                >
                  <TextField type={"number"} onChange={handleBarCodeOnChange} />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={5}>
                <Alert
                  style={{ marginTop: "24px" }}
                  type={barCodeStatusText === "MATCH" ? "success" : "error"}
                  show={barCodeStatusText && barCodeStatusText !== ""}
                  message={
                    <div style={{ textAlign: "center" }}>
                      <b>{barCodeStatusText}</b>
                    </div>
                  }
                  isDismissible={false}
                />
              </StyledGridCol>
            </StyledGridRow>

            {/* ── Dispensation Information ─────────────────────────── */}
            <StyledSectionHeading>
              <span>Dispensation Information</span>
            </StyledSectionHeading>
            <StyledGridRow gutter={16}>
              <StyledGridCol>
                <SecureFormItem
                  fieldName="dateTimeDispensed"
                  formItemProps={{ name: "dateTimeDispensed", label: "Date & Time Dispensed" }}
                >
                  <CommonDatePicker showTime={true} format={"MM/DD/YYYY HH:mm"} />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={6}>
                <SecureFormItem
                  fieldName="dispensedByStaffID"
                  formItemProps={{ name: "dispensedByStaffID", label: "Dispensing Officer" }}
                >
                  <Select
                    options={getOptionsFromRefList("", true)}
                    placeholder="Dispensing Officer"
                    showSearch={true}
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={6}>
                <SecureFormItem
                  fieldName="numberofPills"
                  formItemProps={{
                    name: "numberofPills",
                    label: "# Pills",
                    rules: [{ required: true, message: "# Pills is required" }],
                  }}
                >
                  <TextField type={"number"} />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={24}>
                <SecureFormItem
                  fieldName="comments"
                  formItemProps={{ name: "comments", label: "Comments" }}
                >
                  <TextField inputType="textarea" />
                </SecureFormItem>
              </StyledGridCol>
            </StyledGridRow>

            {/* ── Additional Log Information ───────────────────────── */}
            {medicationLogExtraFields.length > 0 && (
              <>
                <StyledSectionHeading>
                  <span>Additional Log Information</span>
                </StyledSectionHeading>
                <StyledGridRow gutter={16}>
                  {medicationLogExtraFields.map((sf, index) => (
                    <DynamicForm
                      key={index}
                      formAttributes={sf}
                      form={medicationLogForm}
                      bookingsFieldsEnabled={bookingsFieldsEnabled}
                    />
                  ))}
                </StyledGridRow>
              </>
            )}

            {/* ── Administrative Information ───────────────────────── */}
            <StyledSectionHeading>
              <span>Administrative Information</span>
            </StyledSectionHeading>
            <StyledGridRow gutter={16}>
              <StyledGridCol lg={4}>
                <SecureFormItem
                  fieldName="pinNo"
                  formItemProps={{ name: "pinNo", label: "ID" }}
                >
                  <TextField type={"number"} />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={6}>
                <SecureFormItem
                  fieldName="dateTimeAdded"
                  formItemProps={{ name: "dateTimeAdded", label: "Date & Time Entered" }}
                >
                  <CommonDatePicker showTime={true} format={"MM/DD/YYYY HH:mm"} />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="user"
                  formItemProps={{ name: "user", label: "User" }}
                >
                  <TextField />
                </SecureFormItem>
              </StyledGridCol>
            </StyledGridRow>

          </Form>
        </FieldSecurityProvider>
      </Modal>
    </>
  );
};

export default InmateMedicationLogDetail;